import { CircularProgressContinuous } from '@/components/loaders/CircularProgressContinuous'
import { InlineMessage } from '@/components/inline-message/InlineMessage'
import { selectDisplayCostPerSeatInCents } from '@/account/store/selectors'
import { useMembersRosterData } from '@/organizations/hooks/useMembersRosterData'
import { OrgBillingStats } from '@/organizations/views/OrgBillingStats'
import { RootState } from '@/store/store'
import { useAppSelector } from '@/store/store-hooks'
import { formatCentsToDollarsCompact } from '@/util/money'

import { SettingsPanelShell } from '../components/SettingsPanelShell'
import { SubscriptionDetailsCard } from '../components/SubscriptionDetailsCard'

/**
 * Billing section (org admins only). Shows the Subscription Details card — the plan total, Manage
 * Subscription (org payer only), and a Browse all plans link — above the four billing figures, split
 * out from the Members roster so each nav entry owns one concern. Reuses useMembersRosterData for the
 * billing figures and manage-subscription affordance (which also refreshes payment details on mount).
 * The standalone sidebar MemberRosterModal keeps its combined roster + billing view and is unaffected.
 */
export function BillingPanel() {
  // Billing is a dedicated billing surface, so force a fresh payment-details pull on open.
  const { isOrgAdmin, isError, billingStats, onManageSubscription } = useMembersRosterData({ forcePaymentRefresh: true })

  const seatCostCents = useAppSelector(selectDisplayCostPerSeatInCents)
  const subscriptionInterval = useAppSelector((state: RootState) => state.accountState.stripeSubscriptionInterval)
  const isLoading = useAppSelector((state: RootState) => state.accountState.loading)
  const isInitialLoadComplete = useAppSelector((state: RootState) => state.accountState.initialLoadComplete)
  // orgDetails (pending invites) and the payment-details fetch both back this card; surface either failure.
  const paymentError = useAppSelector((state: RootState) => state.accountState.error)
  const showLoader = isLoading && !isInitialLoadComplete

  // Defense in depth: the nav only surfaces Billing to org admins.
  if (!isOrgAdmin) return null

  // Recurring plan price = the same Paid seats × Seat cost shown in the grid below, so the card
  // reconciles with itself. (Deliberately not the upcoming-invoice amount, which also folds in
  // proration, credits, and temporary promos and would disagree with the figures beside it.)
  const paidSeats = billingStats?.paidSeats
  const planTotal =
    paidSeats != null && seatCostCents != null && subscriptionInterval
      ? `${formatCentsToDollarsCompact(paidSeats * seatCostCents)} / ${subscriptionInterval}`
      : '—'

  return (
    <SettingsPanelShell title="Billing">
      {showLoader ? (
        <div className="flex min-h-[40vh] items-center justify-center">
          <CircularProgressContinuous />
        </div>
      ) : isError || paymentError ? (
        <InlineMessage variant="error">Could not load billing details. Refresh the page and try again.</InlineMessage>
      ) : (
        <div className="flex flex-col gap-4">
          <SubscriptionDetailsCard planName="Organization plan" planPrice={planTotal} onManageSubscription={onManageSubscription} />

          {billingStats && <OrgBillingStats billingStats={billingStats} />}
        </div>
      )}
    </SettingsPanelShell>
  )
}
